import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { collectFiles, jsonFiles, zipFiles } from '@chess/api-storage/archive';
import { LOG_API, type Log } from '@chess/api-log';
import { ExportSection, type ExportStatus } from './ExportSection.tsx';
import './settings-export.css';

// "Your data" on the settings screen: reads every file of the storage API
// (games, scans, settings) and downloads them as one ZIP archive, or as one
// JSON document if the archive cannot be made. Nothing leaves the browser
// except through the user's own download.

/** Hands bytes to the browser as a file download. */
function download(data: BlobPart, type: string, name: string): void {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click has been handled, so the download can start.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const stamp = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function setup(app: PiletApi) {
  const log = app.getApi<Log>(LOG_API).then((l) => l.child(app.meta.name));
  const storage = app.getApi<Storage>(STORAGE_API);

  const Section: React.FC = () => {
    const [status, setStatus] = React.useState<ExportStatus>({ kind: 'idle' });
    const live = React.useRef(true);
    React.useEffect(() => () => void (live.current = false), []);
    const set = (s: ExportStatus) => live.current && setStatus(s);

    const onDownload = async () => {
      set({ kind: 'reading', files: 0 });
      try {
        const files = await collectFiles(await storage, '', (n) => set({ kind: 'reading', files: n }));
        const now = new Date();
        const name = `chess-data-${stamp(now)}`;
        try {
          const zip = zipFiles(files, now);
          download(zip, 'application/zip', `${name}.zip`);
          set({ kind: 'done', files: files.length, format: 'zip', bytes: zip.length });
        } catch (zipError) {
          void log.then((l) => l.warn('zip export failed, downloading JSON instead', { error: String(zipError) }));
          const json = jsonFiles(files, now);
          download(json, 'application/json', `${name}.json`);
          set({ kind: 'done', files: files.length, format: 'json', bytes: new TextEncoder().encode(json).length });
        }
      } catch (error) {
        void log.then((l) => l.error('data export failed', { error: String(error) }));
        set({ kind: 'error', message: error instanceof Error ? error.message : String(error) });
      }
    };

    return <ExportSection status={status} onDownload={() => void onDownload()} />;
  };

  app.registerExtension('settings.sections', Section, { order: 90 });
}
